import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { store } from '@/store/store';
import { logout } from '@/store/features/auth/authSlice';
import { APIResponse } from '../types';

export class ApiError<T = unknown> extends Error {
  constructor(
    public message: string,
    public status: number = 500,
    public data?: T,
  ) {
    super(message);
    this.name = 'ApiError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

const httpClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL as string,
  timeout: 150000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

httpClient.interceptors.response.use(
  (response: AxiosResponse<APIResponse>) => {
    const success =
      typeof response.data.success === 'boolean'
        ? response.data.success
        : response.status >= 200 && response.status < 300;

    return {
      ...response,
      data: {
        success,
        message: response.data.message,
        data: response.data,
        status: response.status,
      },
    };
  },
  (error: AxiosError<APIResponse>): Promise<never> => {
    const status = error.response?.status ?? 500;
    const message = error.response?.data.message || error.message;
    const errorData = error.response?.data.data;

    if (status === 401) {
      store.dispatch(logout());
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }

    return Promise.reject(new ApiError(message, status, errorData));
  },
);

export function handleApiResponse<T>(response: AxiosResponse<APIResponse<T>>): T {
  const status = response.status || 500;
  if (!(status >= 200 && status < 300)) {
    throw new ApiError(response.data.message, response.status, response.data);
  }

  if (typeof response.data === 'undefined') {
    throw new ApiError('No data received', response.status);
  }

  return response.data.data as T;
}

export function handleApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (axios.isAxiosError<APIResponse>(error)) {
    return new ApiError(
      error.response?.data.message || error.message,
      error.response?.status,
      error.response?.data.data,
    );
  }

  if (error instanceof Error) {
    return new ApiError(error.message);
  }

  return new ApiError('Unknown error occurred');
}

if (import.meta.env.DEV) {
  httpClient.interceptors.request.use((config) => {
    const method = (config.method?.toUpperCase() ?? 'GET').padEnd(7);
    const url = config.url ?? '/unknown-endpoint';
    console.log(`Request: ${method} ${url}`);
    return config;
  });

  httpClient.interceptors.response.use(
    (response) => {
      const status = response.status.toString().padStart(3);
      const url = response.config.url ?? '/unknown-endpoint';
      console.log(`Response: ${status} ${url}`);
      return response;
    },
    (error) => {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error(`API Error: ${message}`);
      return Promise.reject(error instanceof Error ? error : new ApiError(message));
    },
  );
}

export default httpClient;
